// Local Imports
import { exhaust } from "CoreUtils";
import { content, schema } from "GenAlphaSchema";
import { ContentKind } from "GenAlphaModel";
import { LayoutCell } from "./layout";
import { Schematic } from "./schematic";

export const ViewKind = {
  schematic: "schematic",
  symbol: "symbol",
  layout: "layout",
  graphics: "graphics",
} as const;

export type ViewKind = (typeof ViewKind)[keyof typeof ViewKind];

export const contentKindToViewKind = (contentKind: ContentKind): ViewKind => {
  switch (contentKind) {
    case ContentKind.Schematic:
      return ViewKind.schematic;
    case ContentKind.Symbol:
      return ViewKind.symbol;
    case ContentKind.Layout:
      return ViewKind.layout;
    default:
      throw new Error(`Unexpected content kind: ${contentKind}`);
  }
};

export const viewKindToContentKind = (viewKind: ViewKind): ContentKind => {
  switch (viewKind) {
    case ViewKind.schematic:
      return ContentKind.Schematic;
    case ViewKind.symbol:
      return ContentKind.Symbol;
    case ViewKind.layout:
      return ContentKind.Layout;
    default:
      throw new Error(`Unexpected view kind: ${viewKind}`);
  }
};

export class Cell {
  //
  constructor(public defaultKind: ViewKind = ViewKind.layout) {}
  public schematic?: schema.Schematic = undefined;
  public symbol?: schema.Symbol = undefined;
  public layout?: schema.Layout = undefined;

  // Get a view
  readonly get = (kind_?: ViewKind): schema.Content | undefined => {
    const kind = kind_ || this.defaultKind;
    switch (kind) {
      case ViewKind.schematic:
        return this.schematic;
      case ViewKind.symbol:
        return this.symbol;
      case ViewKind.layout:
        return this.layout;
      case ViewKind.graphics:
        // Graphics view not yet implemented
        return undefined;
      default:
        exhaust(kind);
    }
  };

  // Load a new piece of content into one of our views
  readonly load = (
    content: schema.Content,
    makeDefault: boolean = false
  ): this => {
    switch (content.contentKind) {
      case "schematic":
        if (makeDefault) this.defaultKind = ViewKind.schematic;
        this.schematic = content;
        break;
      case "symbol":
        if (makeDefault) this.defaultKind = ViewKind.symbol;
        this.symbol = content;
        break;
      case "layout":
        if (makeDefault) this.defaultKind = ViewKind.layout;
        this.layout = content;
        break;
      default:
        throw new Error(`Unknown content kind: ${content.contentKind}`);
    }
    return this;
  };

  // Create a new cell from an underlying "Content" / "View"
  // Makes `content` the default view
  static fromContent(content: schema.Content): Cell {
    return new Cell().load(content, true);
  }

  // Helper function to populate IDs in a cell (layout or schematic)
  static addIdsToCell(cell: Cell): Cell {
    if (cell.layout) {
      // v4: Layout is a single object, apply IDs to it
      cell.layout = LayoutCell.withIDs(cell.layout);
    }
    if (cell.schematic) {
      cell.schematic = Schematic.withIDs(cell.schematic);
    }
    return cell;
  }
}
